import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Tag, Input, Select, Form,
  Typography, message, Popconfirm, Row, Col, Statistic, Modal,
  InputNumber, Tooltip, Badge, Empty
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SearchOutlined, ClearOutlined,
  ExclamationCircleOutlined, ReloadOutlined, StopOutlined,
  CheckCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import dnsService from '../../services/dnsService';

const { Title } = Typography;
const { Option } = Select;

const DnsBanlist = () => {
  const [banlist, setBanlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    scene: '',
    device_type: ''
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchBanlist = async (params = {}) => {
    setLoading(true);
    try {
      const result = await dnsService.getBanlist({
        page: params.current || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...filters,
        ...params
      });

      if (result.success) {
        setBanlist(result.data.items);
        setPagination({
          ...pagination,
          current: result.data.pagination.current_page,
          pageSize: result.data.pagination.per_page,
          total: result.data.pagination.total
        });
      } else {
        message.error(result.error || '获取黑名单失败');
      }
    } catch (error) {
      message.error('获取黑名单失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const result = await dnsService.getBanlistStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('获取统计信息失败:', error);
    }
  };

  useEffect(() => {
    fetchBanlist();
    fetchStats();
  }, []);

  const handleTableChange = (newPagination) => {
    fetchBanlist({
      current: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };

  const handleSearch = () => {
    fetchBanlist({ current: 1 });
  };

  const handleClearFilters = () => {
    setFilters({ search: '', scene: '', device_type: '' });
    fetchBanlist({ current: 1, search: '', scene: '', device_type: '' });
  };

  const handleDelete = async (id) => {
    try {
      const result = await dnsService.deleteBanlistEntry(id);
      if (result.success) {
        message.success('删除成功');
        fetchBanlist();
        fetchStats();
      } else {
        message.error(result.error || '删除失败');
      }
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的条目');
      return;
    }
    try {
      const result = await dnsService.batchDeleteBanlist(selectedRowKeys);
      if (result.success) {
        message.success(`成功删除 ${result.data.deleted} 条记录`);
        setSelectedRowKeys([]);
        fetchBanlist();
        fetchStats();
      } else {
        message.error(result.error || '批量删除失败');
      }
    } catch (error) {
      message.error('批量删除失败: ' + error.message);
    }
  };

  const handleCleanup = async () => {
    try {
      const result = await dnsService.cleanupBanlist();
      if (result.success) {
        message.success(`成功清理 ${result.data.deleted} 条过期记录`);
        fetchBanlist();
        fetchStats();
      } else {
        message.error(result.error || '清理失败');
      }
    } catch (error) {
      message.error('清理失败: ' + error.message);
    }
  };

  const handleCreate = async (values) => {
    try {
      const result = await dnsService.createBanlistEntry(values);
      if (result.success) {
        message.success('添加成功');
        setIsModalVisible(false);
        form.resetFields();
        fetchBanlist();
        fetchStats();
      } else {
        message.error(result.error || '添加失败');
      }
    } catch (error) {
      message.error('添加失败: ' + error.message);
    }
  };

  const isExpired = (banTime) => {
    return new Date(banTime) < new Date();
  };

  const columns = [
    {
      title: '类型',
      dataIndex: 'device_type',
      key: 'device_type',
      width: 100,
      render: (type) => {
        const colors = { ip: 'blue', domain: 'green', ua: 'orange' };
        const labels = { ip: 'IP', domain: '域名', ua: 'UA' };
        return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
      }
    },
    {
      title: '值',
      dataIndex: 'device_value',
      key: 'device_value',
      ellipsis: true,
      render: (value) => (
        <Tooltip title={value}>
          <code>{value}</code>
        </Tooltip>
      )
    },
    {
      title: '场景',
      dataIndex: 'scene',
      key: 'scene',
      width: 120,
      render: (scene) => {
        const sceneLabels = {
          'blackip': 'IP黑名单',
          'dns_ban': 'DNS封禁',
          'cdn_ban': 'CDN封禁'
        };
        return <Tag>{sceneLabels[scene] || scene}</Tag>;
      }
    },
    {
      title: '封禁原因',
      dataIndex: 'ban_reason',
      key: 'ban_reason',
      ellipsis: true,
      width: 150
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source) => {
        const colors = {
          'ADMIN': 'purple',
          'THREAT_FEED:IPSUM': 'red',
          'AUTO': 'orange'
        };
        return <Tag color={colors[source] || 'default'}>{source}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'ban_time',
      key: 'status',
      width: 100,
      render: (banTime) => {
        const expired = isExpired(banTime);
        return expired ? (
          <Badge status="default" text="已过期" />
        ) : (
          <Badge status="processing" text="生效中" />
        );
      }
    },
    {
      title: '过期时间',
      dataIndex: 'ban_time',
      key: 'ban_time',
      width: 170,
      render: (time) => {
        const date = new Date(time);
        const expired = isExpired(time);
        return (
          <span style={{ color: expired ? '#999' : 'inherit' }}>
            {date.toLocaleString()}
          </span>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (time) => new Date(time).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="确定删除此条目吗？"
          onConfirm={() => handleDelete(record.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small">
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys
  };

  return (
    <div>
      <Title level={2}>DNS 黑名单管理</Title>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="总记录数"
              value={stats.total}
              prefix={<StopOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="生效中"
              value={stats.active}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="已过期"
              value={stats.expired}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card bordered={false}>
            <Statistic
              title="IP类型"
              value={stats.by_type?.ip || 0}
              suffix={`/ ${stats.total}`}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="黑名单列表"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              添加封禁
            </Button>
            <Popconfirm
              title={`确定删除选中的 ${selectedRowKeys.length} 条记录吗？`}
              onConfirm={handleBatchDelete}
              disabled={selectedRowKeys.length === 0}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={selectedRowKeys.length === 0}
              >
                批量删除
              </Button>
            </Popconfirm>
            <Popconfirm
              title="确定清理所有过期记录吗？"
              onConfirm={handleCleanup}
            >
              <Button icon={<ClearOutlined />}>
                清理过期
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="搜索 IP/域名/UA"
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="场景"
                style={{ width: '100%' }}
                value={filters.scene || undefined}
                onChange={(value) => setFilters({ ...filters, scene: value })}
                allowClear
              >
                <Option value="blackip">IP黑名单</Option>
                <Option value="dns_ban">DNS封禁</Option>
                <Option value="cdn_ban">CDN封禁</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="类型"
                style={{ width: '100%' }}
                value={filters.device_type || undefined}
                onChange={(value) => setFilters({ ...filters, device_type: value })}
                allowClear
              >
                <Option value="ip">IP</Option>
                <Option value="domain">域名</Option>
                <Option value="ua">User-Agent</Option>
              </Select>
            </Col>
            <Col span={6}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  搜索
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleClearFilters}>
                  重置
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={banlist}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: <Empty description="暂无封禁记录" />
          }}
        />
      </Card>

      <Modal
        title="添加封禁"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="device_type"
            label="类型"
            rules={[{ required: true, message: '请选择类型' }]}
          >
            <Select placeholder="选择封禁类型">
              <Option value="ip">IP 地址</Option>
              <Option value="domain">域名</Option>
              <Option value="ua">User-Agent</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="device_value"
            label="值"
            rules={[{ required: true, message: '请输入要封禁的值' }]}
          >
            <Input placeholder="如: 1.2.3.4 或 192.168.0.0/24" />
          </Form.Item>

          <Form.Item
            name="scene"
            label="场景"
            rules={[{ required: true, message: '请选择场景' }]}
          >
            <Select placeholder="选择封禁场景">
              <Option value="blackip">IP黑名单 (DNS查询)</Option>
              <Option value="dns_ban">DNS封禁</Option>
              <Option value="cdn_ban">CDN封禁</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="ban_duration"
            label="封禁时长 (天)"
            initialValue={30}
            rules={[{ required: true, message: '请输入封禁时长' }]}
          >
            <InputNumber min={1} max={3650} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="ban_reason"
            label="封禁原因"
          >
            <Input.TextArea rows={2} placeholder="输入封禁原因 (可选)" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                添加
              </Button>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DnsBanlist;
